import { useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

interface SilentCodeForwarderProps {
	callback: (code: string) => void;
	onError?: (error: string) => void;
}

export interface SilentCodeForwarderRef {
	startSilentAuth: () => void;
	reset: () => void;
}

export const SilentCodeForwarder = forwardRef<SilentCodeForwarderRef, SilentCodeForwarderProps>(
	({ callback, onError }, ref) => {
		const hasAttempted = useRef(false);
		const iframeRef = useRef<HTMLIFrameElement | null>(null);
		const timeoutRef = useRef<NodeJS.Timeout | null>(null);
		const iframeDomDelay = 100;
		const iframeCleanupDelay = 5000;

		const cleanup = useCallback(() => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			window.removeEventListener('message', handleMessage);
			if (iframeRef.current && document.body.contains(iframeRef.current)) {
				document.body.removeChild(iframeRef.current);
				iframeRef.current = null;
			}
		}, []);

		const handleMessage = useCallback(
			(event: MessageEvent) => {
				if (event.origin !== window.location.origin) return;

				if (event.data?.type === 'SILENT_AUTH_RESULT') {
					const { code, error } = event.data;
					cleanup();

					if (error) {
						console.warn('Silent authentication failed:', error);
						onError?.(error);
					} else if (code) {
						callback(code);
					}
				} else if (event.data?.type === 'CLOSE_IFRAME') {
					// Handle iframe close request
					cleanup();
				}
			},
			[callback, onError, cleanup],
		);

		const startSilentAuth = useCallback(() => {
			if (hasAttempted.current) {
				console.warn('Silent authentication already attempted');
				return;
			}

			hasAttempted.current = true;

			const iframe = document.createElement('iframe');
			iframe.style.display = 'none';
			iframe.src =
				`${import.meta.env.VITE_AUTH_SERVER}/realms/${import.meta.env.VITE_AUTH_REALM}/protocol/openid-connect/auth?` +
				`client_id=${import.meta.env.VITE_BOOST_AI_AUTHENTICATION_CLIENT}&` +
				`response_type=code&` +
				`scope=openid profile&` +
				`redirect_uri=${encodeURIComponent(window.location.origin + '/silent-callback.html')}&` +
				`prompt=none`;

			iframeRef.current = iframe;

			// Add iframe to DOM after a short delay
			setTimeout(() => {
				if (iframeRef.current) {
					document.body.appendChild(iframeRef.current);
				}
			}, iframeDomDelay);

			// Set timeout for cleanup
			timeoutRef.current = setTimeout(() => {
				cleanup();
				console.warn('Silent authentication timed out');
				onError?.('Authentication timed out');
			}, iframeCleanupDelay);

			window.addEventListener('message', handleMessage);
		}, [handleMessage, cleanup, onError]);

		const reset = useCallback(() => {
			hasAttempted.current = false;
			cleanup();
		}, [cleanup]);

		// Expose the start function via ref
		useImperativeHandle(ref, () => ({
			startSilentAuth,
			reset,
		}));

		return null;
	},
);

SilentCodeForwarder.displayName = 'SilentCodeForwarder';
